import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';
import ProjectCard from '../../components/ProjectCard/ProjectCard';
import './UserProfilePage.css';

const FALLBACK_COLORS = ['#0ea5e9', '#6366f1', '#f97316', '#ef4444', '#10b981', '#8b5cf6'];

function deriveHandle(profile) {
  const fromDisplay = profile?.displayName?.trim();
  if (fromDisplay) {
    return `@${fromDisplay.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  }

  const email = profile?.email || '';
  const prefix = email.split('@')[0];
  if (prefix) {
    return `@${prefix.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  }

  return '@origami-builder';
}

function deriveInitial(profile) {
  const base = profile?.displayName || profile?.email || 'O';
  return base.trim().charAt(0).toUpperCase() || 'O';
}

function colorFromInitial(initial) {
  const code = initial.charCodeAt(0);
  return FALLBACK_COLORS[code % FALLBACK_COLORS.length];
}

function formatDate(timestamp) {
  if (!timestamp) return null;
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime())
      ? null
      : parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  if (timestamp.toDate) {
    const asDate = timestamp.toDate();
    return asDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return null;
}

function UserProfilePage() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id');

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState('');

  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setCurrentUser(authUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchProjects = useCallback(async () => {
    if (!userId) {
      setProjects([]);
      setProjectsLoading(false);
      return;
    }

    setProjectsError('');
    setProjectsLoading(true);

    const projectsRef = collection(db, 'marketplace-projects');

    try {
      const filteredQuery = query(projectsRef, where('ownerId', '==', userId), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(filteredQuery);
      const items = snapshot.docs.map((projectDoc) => ({ id: projectDoc.id, ...projectDoc.data() }));
      setProjects(items);
    } catch (err) {
      console.error('Error loading user projects with ordering, falling back without order', err);
      if (err.code === 'failed-precondition') {
        try {
          const fallbackSnapshot = await getDocs(query(projectsRef, where('ownerId', '==', userId)));
          const items = fallbackSnapshot.docs.map((projectDoc) => ({ id: projectDoc.id, ...projectDoc.data() }));
          setProjects(items);
        } catch (fallbackErr) {
          console.error('Fallback project load failed', fallbackErr);
          setProjects([]);
          setProjectsError('We could not load this builder\'s projects.');
        }
      } else {
        setProjects([]);
        setProjectsError('We could not load this builder\'s projects.');
      }
    } finally {
      setProjectsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setProfileError('Missing user id in the URL.');
      setProfileLoading(false);
      setProjects([]);
      setProjectsLoading(false);
      return;
    }

    let active = true;
    setProfileLoading(true);
    setProfileError('');

    const loadProfile = async () => {
      try {
        const userRef = doc(db, 'users', userId);
        const snapshot = await getDoc(userRef);
        if (!snapshot.exists()) {
          if (!active) return;
          setProfile(null);
          setProfileError('We could not find that profile.');
          return;
        }

        if (!active) return;
        setProfile({ id: userId, ...snapshot.data() });
      } catch (err) {
        console.error('Error loading profile', err);
        if (!active) return;
        setProfile(null);
        setProfileError('Something went wrong while loading this profile.');
      } finally {
        if (active) {
          setProfileLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const displayName = useMemo(() => {
    if (profile?.displayName) return profile.displayName;
    if (profile?.email) return profile.email.split('@')[0];
    return 'Origami builder';
  }, [profile]);

  const profileInitial = useMemo(() => deriveInitial(profile), [profile]);
  const avatarColor = useMemo(() => colorFromInitial(profileInitial), [profileInitial]);
  const handle = useMemo(() => deriveHandle(profile), [profile]);
  const joinedLabel = useMemo(() => formatDate(profile?.createdAt), [profile]);
  const lastActiveLabel = useMemo(() => formatDate(profile?.lastLoginAt), [profile]);

  const isOwnProfile = Boolean(currentUser && userId && currentUser.uid === userId);
  const projectCount = projects.length;

  return (
    <div className="profile-shell">
      <section className="profile-hero">
        <div className="layout-container profile-hero__inner">
          <div className="profile-hero__avatar" style={{ background: avatarColor }} aria-hidden="true">
            {profileInitial}
          </div>

          <div className="profile-hero__content">
            {profileLoading ? (
              <div className="profile-status">Loading profile…</div>
            ) : profileError ? (
              <div className="profile-error" role="alert">{profileError}</div>
            ) : (
              <>
                <div className="profile-hero__meta">
                  <span className="profile-handle">{handle}</span>
                  {joinedLabel && <span className="profile-hero__dot">•</span>}
                  {joinedLabel && <span>Joined {joinedLabel}</span>}
                </div>
                <h1>{displayName}</h1>
                <p className="profile-hero__bio">
                  {profile?.bio || 'This builder has not added a bio yet, but their projects speak for themselves.'}
                </p>
                <div className="profile-hero__stats">
                  <div className="profile-stat">
                    <span className="profile-stat__value">{projectCount}</span>
                    <span className="profile-stat__label">Projects shared</span>
                  </div>
                  {lastActiveLabel && (
                    <div className="profile-stat">
                      <span className="profile-stat__value">{lastActiveLabel}</span>
                      <span className="profile-stat__label">Last active</span>
                    </div>
                  )}
                </div>
                <div className="profile-hero__actions">
                  <Link to="/marketplace" className="profile-action profile-action--primary">
                    Browse marketplace
                  </Link>
                  {profile?.email && (
                    <a
                      href={`mailto:${profile.email}`}
                      className="profile-action profile-action--ghost"
                    >
                      Contact
                    </a>
                  )}
                  {isOwnProfile && (
                    <Link to="/login" className="profile-action profile-action--ghost">
                      Manage account
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {profileError && !profileLoading ? (
        <section className="profile-empty">
          <div className="layout-container">
            <p className="profile-error" role="alert">{profileError}</p>
          </div>
        </section>
      ) : (
        <section className="profile-projects">
          <div className="layout-container">
            <div className="profile-projects__header">
              <h2>Projects</h2>
              <span className="profile-projects__count">{projectCount} shared</span>
            </div>
            {projectsLoading ? (
              <div className="profile-status">Loading projects…</div>
            ) : projectsError ? (
              <div className="profile-error" role="alert">{projectsError}</div>
            ) : projectCount === 0 ? (
              <div className="profile-empty">
                <h3>No projects yet</h3>
                <p>This builder has not published any marketplace projects just yet.</p>
                {isOwnProfile && (
                  <Link to="/marketplace" className="profile-action profile-action--primary">
                    Share your first project
                  </Link>
                )}
              </div>
            ) : (
              <div className="profile-projects__grid">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    currentUser={currentUser}
                    onProjectDeleted={fetchProjects}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default UserProfilePage;
